import { Category } from "@/dto/category";
import { Item } from "@/dto/item";

type Variant = {
  id: string;
  name: string;
  options: { id: string; name: string }[];
};

type DefaultOptions = {
  [key: string]: string;
};

export const getDefaultItemAndOptions = (
  items?: Item[],
  variants?: Variant[]
): { defaultItem: Item | null; defaultOptions: DefaultOptions } => {
  if (!items || items.length === 0) {
    return { defaultItem: null, defaultOptions: {} };
  }

  const defaultItem = items.find((item) => item.available) ?? items[0];
  let defaultOptions: DefaultOptions = {};

  if (defaultItem.options && variants) {
    defaultOptions = defaultItem.options.reduce(
      (acc: DefaultOptions, option) => {
        const variant = variants.find((v) =>
          v.options.some((o) => o.id === option.id)
        );
        if (variant) {
          acc[variant.id] = option.id;
        }
        return acc;
      },
      {}
    );
  }

  return { defaultItem, defaultOptions };
};

/**
 * Filters a list of restaurant categories to determine which should be displayed.
 * * The filtering logic preserves categories if they meet any of the following criteria:
 * 1. They are internal-use categories ("uncategorised" or "GIFTCARD").
 * 2. They contain one or more items (checked via `numberOfItems` or the `items` array length).
 * * @param {Category[]} categoriesList - The array of category objects to be filtered.
 * @returns {Category[]} A filtered array of categories. Returns an empty array if the input is null or undefined.
 * * @example
 * const visibleCategories = filterRestaurantCategories(allCategories);
 */
export const filterRestaurantCategories = (categoriesList: Category[]) => {
  if (!categoriesList) return [];
  return categoriesList.filter(
    (c: Category) =>
      c?.id === "uncategorised" ||
      c?.id === "GIFTCARD" ||
      (c.numberOfItems === undefined
        ? (c.items?.length || 0) > 0
        : c.numberOfItems > 0)
  );
};
